import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { css } from '@compiled/react';
import { useLoaderData, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ContentType, Heading, InformationCardState, LearningControlPanel } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';

import { LessonListWrapper, CoursePageInformationCard } from 'src/pages/CoursePage/children';
import { ContentHeader } from 'src/components/ContentHeader';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { HelmetContext } from 'src/components/Helmet';
import { sendScreenEvent } from 'src/utils/analytics';
import { getSlug } from 'src/utils/slug';
import { getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { getAssetUrl } from 'src/services/http/user';
import { LearningProgressStatus } from 'src/services/http/progress';
import { useProgressState, useLessonProgress } from 'src/store/progress/progressStore';
import { useSaveRecentlyViewed } from 'src/hooks/useSaveRecentlyViewed';
import { useReferrer } from 'src/hooks/useReferrer';
import { useRedirectObject } from 'src/hooks/useRedirectObject';
import { useSaveContent } from 'src/hooks/useSaveContent';
import { useStartContent } from 'src/hooks/useStartContent';
import { CourseLoaderProps } from 'src/pages/CoursePage/loader';
import { VersioningModalWrapper } from 'src/components/VersioningModalWrapper';
import { Disclaimer } from 'src/components/Disclaimer';
import { FIELD_ID } from 'src/pages/CoursePage/utils';

export const CoursePage: React.FC = () => {
  const params = useParams();
  const { course, learningPath, notifications } = useLoaderData() as CourseLoaderProps;
  const { user, isLoading, hasError } = useAtlassianUser();
  const location = useLocation();
  const currentUrl = globalThis?.location?.href;
  const navigate = useNavigate();
  const helmet = useContext(HelmetContext);
  const [, { fetchProgressForLessons }] = useProgressState();
  const [{ progressForLessons, isLoadingProgressForLessons }] = useLessonProgress();
  const { referrerPath, isInitialized } = useReferrer();
  const { isContentSaved, saveContent } = useSaveContent({
    contentId: course?.sys.id,
    contentType: ContentType.COURSE,
    learningPathId: learningPath?.sys.id,
    user,
  });
  const courseSlug = getSlug(course);
  const lessonListRef = useRef<HTMLDivElement | null>(null);

  useRedirectObject({
    ':courseId': courseSlug,
    ...(params?.pathId && { ':pathId': getSlug(learningPath) }),
  });

  helmet.setHelmet({
    title: `${course.title} | Learning - Atlassian Community`,
    seoTags: course.seo,
  });

  useEffect(() => {
    if (!isLoading && !hasError && course && isInitialized) {
      sendScreenEvent(
        'courseScreen',
        {
          courseSlug: course.slug,
          courseReferrerPath: referrerPath,
        },
        user?.account_id,
      );
    }
  }, [user, isLoading, hasError, course, referrerPath, isInitialized]);

  useEffect(() => {
    if (!isLoading && !hasError && course && isInitialized) {
      sendScreenEvent('courseScreen', { courseSlug, learningReferrerPath: referrerPath }, user?.account_id);
    }
  }, [user, isLoading, hasError, course, courseSlug, referrerPath, isInitialized]);

  useEffect(() => {
    if (user && course) {
      const currentLessonId = progressForLessons?.lessons[0]?.lessonId;
      const lessonIds = course?.lessonsCollection.items.map((l) => l.sys.id);

      if (!progressForLessons || (currentLessonId && currentLessonId !== lessonIds[0])) {
        fetchProgressForLessons(lessonIds, user, course.sys.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, user]);

  useSaveRecentlyViewed(course?.sys.id, ContentType.COURSE, learningPath?.sys.id);

  const getLessonState = (): InformationCardState => {
    if (isLoading || isLoadingProgressForLessons) {
      return InformationCardState.LOADING;
    }

    if (!progressForLessons || !user) {
      return InformationCardState.DEFAULT;
    }

    if (progressForLessons?.overallStatus === LearningProgressStatus.IN_PROGRESS) {
      return InformationCardState.IN_PROGRESS;
    }

    if (progressForLessons?.overallStatus === LearningProgressStatus.COMPLETED) {
      return InformationCardState.FINISHED;
    }

    return InformationCardState.DEFAULT;
  };

  const navigateToLesson = useCallback(
    (lessonSlug: string): void => {
      navigate(withPrefix(getLessonUrl({ lessonSlug }), location.pathname.replace(/\/$/, '')));
    },
    [navigate, location],
  );

  const startCourse = useStartContent({
    items: course?.lessonsCollection?.items,
    progress: progressForLessons,
    contentSlug: courseSlug,
    contentType: ContentType.COURSE,
    userId: user?.account_id,
    navigateToItem: navigateToLesson,
    fieldId: FIELD_ID,
  });

  return (
    <>
      <VersioningModalWrapper notifications={notifications} resourceTitle={course.title} />
      <main css={courseMainContainerStyles} data-testid="course-page">
        <div
          css={[courseBannerStyles]}
          style={
            {
              '--desktop-banner': `url(${getAssetUrl('/images/contentBanners/coursePageBanner.svg')})`,
              '--mobile-banner': `url(${getAssetUrl('/images/contentBanners/mobileCoursePageBanner.svg')})`,
            } as Record<string, string>
          }
          role="img"
          aria-label="Course Banner"
        />
        <div css={courseMainContentStyles}>
          <div css={courseFirstColumnStyles}>
            <div css={courseContentContainerStyles} data-testid="main-content-container">
              <div css={contentHeaderStyles}>
                <ContentHeader content={course} contentType={ContentType.COURSE} learningPath={learningPath} />
              </div>
            </div>
            <div css={courseInfoCardStyles}>
              <CoursePageInformationCard urlToShare={currentUrl} />
            </div>
            <div css={courseCenteredContentStyles}>
              <div css={courseLessonListWrapperStyles}>
                <div css={courseLessonsContentStyles} data-testid="lesson-list-container">
                  <Heading level={4} color={token('color.text')}>
                    Lessons list
                  </Heading>
                  <div ref={lessonListRef} css={courseWrapperStyles}>
                    <LessonListWrapper />
                  </div>
                </div>
              </div>
            </div>
            <Disclaimer disclaimer={course?.disclaimer} entryId={course?.sys.id} contentType={ContentType.COURSE} />
          </div>
          <div css={courseAbsoluteInformationCardStyles}>
            <CoursePageInformationCard urlToShare={currentUrl} />
          </div>
        </div>
        <div css={courseStickyControlPanelStyles}>
          <LearningControlPanel
            isSaved={isContentSaved}
            onSave={user && saveContent}
            urlToShare={currentUrl}
            state={getLessonState()}
            contentType={ContentType.COURSE}
            onClick={startCourse}
          />
        </div>
      </main>
    </>
  );
};

const courseMainContainerStyles = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'clip',
});

const courseMainContentStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  gap: token('space.300'),
  maxWidth: '1200px',
  margin: '0 auto',
  justifyContent: 'flex-start',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  padding: `0 ${token('space.300')}`,

  '@media (max-width: 965px)': {
    flexDirection: 'column',
    padding: `0 ${token('space.200')}`,
    gap: token('space.200'),
  },
});

const courseBannerStyles = css({
  minHeight: '119px',
  width: '100%',
  objectFit: 'cover',
  backgroundImage: 'var(--desktop-banner)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  marginTop: '136px',

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },

  '@media (max-width: 768px)': {
    marginTop: '0',
  },

  '@media (max-width: 965px)': {
    backgroundImage: 'var(--mobile-banner)',
    minHeight: '60px',
  },
});

const courseContentContainerStyles = css({
  display: 'flex',
  gap: token('space.300'),
  maxWidth: '1200px',
  margin: '0 auto',
  paddingTop: '45px',
  width: '100%',
  position: 'relative',
  zIndex: 1,

  '@media (max-width: 1200px)': {
    flexDirection: 'column',
    gap: token('space.300'),
  },
});

const courseFirstColumnStyles = css({
  display: 'flex',
  flex: '1 1 0',
  minWidth: 0,
  flexDirection: 'column',
  maxWidth: '100%',

  '@media (max-width: 965px)': {
    width: '100%',
    maxWidth: '100%',
    order: 1,
  },
});

const contentHeaderStyles = css({
  display: 'flex',
  flex: 1,

  '@media (max-width: 965px)': {
    width: '100%',
    order: 1,
  },
});

const courseInfoCardStyles = css({
  display: 'none',

  '@media (max-width: 965px)': {
    display: 'block',
    width: '100%',
    order: 2,
    paddingBottom: token('space.300'),
  },
});

const courseAbsoluteInformationCardStyles = css({
  position: 'sticky',
  top: '65px',
  flex: '1 1 0',
  minWidth: '250px',
  maxWidth: '395px',
  width: 'auto',
  borderRadius: '8px',
  zIndex: 2,
  alignSelf: 'flex-start',
  paddingTop: '45px',
  paddingBottom: token('space.300'),

  '@media (max-width: 965px)': {
    display: 'none',
  },
});

const courseCenteredContentStyles = css({
  padding: `${token('space.300')} 0`,
  display: 'flex',
  gap: token('space.300'),
  width: '100%',
  position: 'relative',
  zIndex: 1,

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    width: '200vw',
    height: '100%',
    transform: 'translateX(-50%)',
    background: 'linear-gradient(90deg, #F8F8F8 0%, #F8F8F8 49.58%, rgba(248, 248, 248, 0.25) 100%)',
    zIndex: -1,
    pointerEvents: 'none',
  },

  '@media (max-width: 1200px)': {
    order: 3,
  },

  '@media (max-width: 965px)': {
    order: 3,
  },
});

const courseLessonsContentStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  gap: token('space.200'),
  position: 'relative',
  zIndex: 1,

  '@media (max-width: 965px)': {
    minHeight: 'unset',
  },
});

const courseWrapperStyles = css({
  width: '100%',
});

const courseLessonListWrapperStyles = css({
  position: 'relative',
  width: '100%',
});

const courseStickyControlPanelStyles = css({
  display: 'none',

  '@media (max-width: 965px)': {
    display: 'block',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: token('color.text.inverse'),
    zIndex: 1000,
  },
});
